'use client';
import AccountContentHeader from '@/components/layout/AccountContentHeader';
import AccountSidebar from '@/components/layout/AccountSidebar';
import BottomTabBar from '@/components/layout/BottomTabBar';
import MoreMenuSheet from '@/components/layout/MoreMenuSheet';
import { BrandyAIProvider } from '@/context/BrandyAIContext';
import { useStateContext } from '@/context/StateContext';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import styles from './AccountLayout.module.scss';

const DynamicCart = dynamic(() => import('@/components/Cart'), { ssr: false });

// Dynamically import BrandyAIRail (client-only component with animations)
const BrandyAIRail = dynamic(() => import('@/components/BrandyAI/BrandyAIRail'), {
  ssr: false,
});

/**
 * AccountLayout Component
 * Wrapper for account area pages
 * Provides two-column layout: sidebar + content
 *
 * @param {Object} session - NextAuth session object
 * @param {React.ReactNode} children - Page content
 * @param {Object} config - Optional layout configuration from page
 * @param {boolean} config.sidebarDefaultCollapsed - Override sidebar initial collapsed state
 * @param {boolean} config.hideHeaderActions - Hide top-right action buttons
 */
const AccountLayout = ({ session, children, config = {} }) => {
  const pathname = usePathname();
  const { showCart } = useStateContext();

  // Mobile detection (768px breakpoint) - avoid hydration mismatch
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Mobile header auto-hide on scroll state
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Wait for client-side mount to avoid SSR hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMounted]);

  // Mobile header auto-hide on scroll
  useEffect(() => {
    if (!isMounted || !isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Always show header at top of page (within 10px)
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }

      // Determine scroll direction
      const scrollingDown = currentScrollY > lastScrollY;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);

      // Only hide/show if scrolled more than 5px (prevents jitter)
      if (scrollDelta < 5) return;

      if (scrollingDown && currentScrollY > 60) {
        // Scrolling down & past threshold → hide header
        setHeaderVisible(false);
      } else if (!scrollingDown) {
        // Scrolling up → show header
        setHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMounted, isMobile, lastScrollY]);

  // Close more menu on route change
  useEffect(() => {
    if (isMobile) {
      setMoreMenuOpen(false);
    }
  }, [pathname, isMobile]);

  return (
    <BrandyAIProvider>
      <div className={styles.accountLayout} suppressHydrationWarning>
        {/* Desktop Sidebar - hidden on mobile */}
        {!isMobile && (
          <AccountSidebar
            session={session}
            pathname={pathname}
            isMobile={false}
            defaultCollapsed={config.sidebarDefaultCollapsed}
          />
        )}

        {/* Mobile Header - Outside contentArea for auto-hide on scroll */}
        {isMounted && isMobile && (
          <AccountContentHeader
            isMobile={isMobile}
            headerVisible={headerVisible}
            hideActions={config.hideHeaderActions}
          />
        )}

        <main className={`${styles.contentArea} ${isMobile ? styles.withBottomNav : ''}`}>
          {showCart && <DynamicCart />}
          {/* Desktop Header - Inside contentArea (absolute positioned) */}
          {!isMobile && (
            <AccountContentHeader
              isMobile={false}
              headerVisible={true}
              hideActions={config.hideHeaderActions}
            />
          )}
          {children}
        </main>
      </div>

      {/* Mobile Bottom Tab Bar - 2026+ pattern */}
      {isMounted && isMobile && <BottomTabBar setMoreMenuOpen={setMoreMenuOpen} />}

      {/* Mobile More Menu Sheet */}
      {isMounted && isMobile && (
        <MoreMenuSheet
          isOpen={moreMenuOpen}
          onClose={() => setMoreMenuOpen(false)}
          session={session}
        />
      )}

      {/* Global Brandy AI Right Rail */}
      <BrandyAIRail />
    </BrandyAIProvider>
  );
};

export default AccountLayout;
